import React, { useEffect, useMemo, useState } from 'react';

import { Button } from '@/design-system/button';
import { FormModal } from '@/design-system/form-modal';
import { SearchSelect } from '@/design-system/search-select';
import { toast } from '@/design-system/toaster';
import { Pitstop, getListPitstop } from '@/services/api/pitstops';
import {
  ISO_DAY_LABELS,
  PitstopScheduleError,
  pitstopSchedulesAPI,
} from '@/services/api/pitstopSchedules';
import { isHhMm, localSlotToUtc, shiftActiveDays } from '@/utils/scheduleTime';

const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

/**
 * "Tambah jadwal" — pick a client pitstop, a wall-clock time, and the days the
 * slot is offered. The slot then shows up in Import Shipments as a selectable
 * dispatch time for that pitstop.
 */
const AddScheduleModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const [pitstops, setPitstops] = useState<Pitstop[]>([]);
  const [pitstopsLoading, setPitstopsLoading] = useState(false);
  const [pitstopId, setPitstopId] = useState('');
  const [dispatchTime, setDispatchTime] = useState('07:00');
  const [days, setDays] = useState<number[]>(ALL_DAYS);
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) {
      setPitstopId('');
      setDispatchTime('07:00');
      setDays(ALL_DAYS);
      setNotes('');
      return;
    }
    let stale = false;
    setPitstopsLoading(true);
    getListPitstop({ type: 'PROVIDER_WAREHOUSE', page: '1', size: '100' })
      .then((res) => {
        if (!stale) setPitstops(res.data ?? []);
      })
      .catch(() => {
        if (!stale) setPitstops([]);
      })
      .finally(() => {
        if (!stale) setPitstopsLoading(false);
      });
    return () => {
      stale = true;
    };
  }, [open]);

  const timeValid = isHhMm(dispatchTime);
  const canSave = Boolean(pitstopId) && timeValid && days.length > 0 && !saving;

  const options = useMemo(
    () =>
      pitstops.map((p) => ({
        value: String(p.id),
        label: p.provider?.name ? `${p.name} · ${p.provider.name}` : p.name,
      })),
    [pitstops]
  );

  const toggleDay = (iso: number) =>
    setDays((prev) =>
      prev.includes(iso)
        ? prev.filter((d) => d !== iso)
        : [...prev, iso].sort((a, b) => a - b)
    );

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    try {
      // Ops picks the slot on their own clock; storage is UTC. A slot near
      // midnight lands on a different UTC weekday, so the day set moves with
      // it — otherwise a 05:30 Senin slot would be stored as running Senin
      // 22:30 UTC, a day late.
      const utc = localSlotToUtc(dispatchTime);
      await pitstopSchedulesAPI.create(Number(pitstopId), {
        dispatchTime: utc.time,
        activeDays: shiftActiveDays(days, utc.dayShift),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
      });
      toast.success(`Jadwal dispatch ${dispatchTime} dibuat`);
      onCreated();
    } catch (err) {
      const e = err as PitstopScheduleError;
      const message =
        e?.code === 'SCHEDULE_ALREADY_EXISTS'
          ? 'Pitstop ini sudah punya jadwal di jam tersebut'
          : e?.code === 'PITSTOP_NOT_CLIENT'
            ? 'Pitstop itu bukan pitstop client (PROVIDER_WAREHOUSE)'
            : e?.code === 'PITSTOP_NO_CLIENT'
              ? 'Pitstop belum punya client di core — lengkapi masterdata dulu'
              : (e?.message ?? 'Coba lagi');
      toast.error('Gagal membuat jadwal', { description: message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <FormModal
      open={open}
      onClose={onClose}
      icon={<i className="ri-time-line" />}
      iconTone="primary"
      title="Tambah jadwal dispatch"
      subtitle="Pilih pitstop client, jam kirim, dan hari aktifnya."
      size="md"
      closeOnBackdropClick={!saving}
      closeOnEscape={!saving}
      footer={
        <div className="flex items-center justify-end gap-2">
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={saving}
          >
            Batal
          </Button>
          <Button onClick={() => void handleSave()} disabled={!canSave}>
            {saving ? (
              <>
                <i className="ri-loader-4-line mr-1.5 animate-spin text-[14px]" />
                Menyimpan…
              </>
            ) : (
              'Simpan'
            )}
          </Button>
        </div>
      }
    >
      <div className="space-y-5 p-5">
        <div>
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
            Pitstop client <span className="text-error-base">*</span>
          </label>
          <SearchSelect
            placeholder="Cari atau pilih pitstop…"
            options={options}
            value={pitstopId}
            onChange={(next) => setPitstopId(String(next))}
            loading={pitstopsLoading}
            disabled={saving}
            emptyMessage="Tidak ada pitstop client"
          />
        </div>

        <div className="flex items-end gap-4">
          <div>
            <label
              htmlFor="add-schedule-time"
              className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
            >
              Jam dispatch <span className="text-error-base">*</span>
            </label>
            <input
              id="add-schedule-time"
              type="time"
              value={dispatchTime}
              onChange={(e) => setDispatchTime(e.target.value)}
              disabled={saving}
              className="h-9 rounded-lg border border-stroke-soft-200 bg-white px-3 font-mono text-sm tabular-nums text-text-strong-950 outline-none transition-colors focus:border-accent disabled:opacity-50"
            />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600">
            Hari aktif <span className="text-error-base">*</span>
          </label>
          <div className="flex flex-wrap gap-1.5">
            {ISO_DAY_LABELS.map((label, i) => {
              const iso = i + 1;
              const on = days.includes(iso);
              return (
                <button
                  key={label}
                  type="button"
                  disabled={saving}
                  onClick={() => toggleDay(iso)}
                  className={`rounded-lg border px-2.5 py-1 text-[12px] font-semibold transition-colors disabled:opacity-50 ${
                    on
                      ? 'border-primary-base bg-primary-alpha-16 text-primary-base'
                      : 'border-stroke-soft-200 text-text-sub-600 hover:border-stroke-sub-300'
                  }`}
                >
                  {label}
                </button>
              );
            })}
          </div>
          {days.length === 0 && (
            <p className="mt-1 text-[11px] text-error-base">
              Pilih minimal satu hari.
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor="add-schedule-notes"
            className="mb-1.5 block text-[10px] font-bold uppercase tracking-[0.18em] text-text-sub-600"
          >
            Catatan
          </label>
          <textarea
            id="add-schedule-notes"
            rows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={saving}
            maxLength={500}
            placeholder="Opsional — mis. batch pagi e-commerce"
            className="w-full rounded-lg border border-stroke-soft-200 bg-white px-3 py-2 text-sm text-text-strong-950 outline-none transition-colors focus:border-accent disabled:opacity-50"
          />
        </div>
      </div>
    </FormModal>
  );
};

export default AddScheduleModal;
